import { useCallback, useEffect, useRef, useState } from "react";
import { roomApi } from "./roomApi";

const PULL_MS = 5000;

/**
 * Loads a session's board, once, on the way in.
 *
 * It no longer saves: changes go out as operations (useBoardOps), which are
 * applied and stored by the server. This is only how a board that already
 * exists gets onto a screen that has just opened — a teacher returning, a
 * student joining halfway through.
 *
 * A student keeps re-reading on a slow interval as a backstop, so a missed
 * operation is corrected within a few seconds rather than leaving their board
 * quietly wrong. The teacher does not: their own canvas is the truth.
 */
export function useBoardSync({ sessionGuid, subRoomGuid = null, board, isInstructor }) {
  const [status, setStatus] = useState("idle"); // idle | loaded
  const revision = useRef(null);
  const loaded = useRef(false);
  const scopeRef = useRef(null);
  const live = Boolean(sessionGuid);

  // What we last loaded or saved. Without this, applying a loaded board
  // counts as a change and saves it straight back, bumping the revision for
  // nothing every time someone opens the room.
  const synced = useRef(null);
  const fingerprint = (items, strokes) => `${items.length}:${strokes.length}:${JSON.stringify(items)}`;

  const apply = useCallback(
    (doc) => {
      revision.current = doc.revision;
      const payload = doc.items || {};
      const items = payload.items || [];
      const strokes = payload.strokes || [];
      synced.current = fingerprint(items, strokes);
      board.replaceAll(items, strokes);
    },
    [board]
  );

  // Load once, then keep a reader in step.
  useEffect(() => {
    if (!live) return undefined;
    let cancelled = false;
    // A different board: start again from what the server has. Only then —
    // this effect also re-runs as the board changes, and starting again
    // each time would load, change the board, and load again, forever.
    const scope = subRoomGuid || "class";
    if (scopeRef.current !== scope) {
      scopeRef.current = scope;
      loaded.current = false;
      revision.current = null;
    }

    const pull = async () => {
      try {
        const doc = await roomApi.getBoard(sessionGuid, subRoomGuid);
        if (cancelled) return;
        // A teacher's own edits must not be undone by their own save coming
        // back, so only pull when the revision has moved past ours.
        if (!loaded.current || (!isInstructor && doc.revision !== revision.current)) {
          apply(doc);
          loaded.current = true;
          setStatus("loaded");
        } else {
          revision.current = Math.max(revision.current ?? 0, doc.revision);
        }
      } catch {
        /* the board keeps what it has */
      }
    };

    pull();
    // The teacher is the class board's only writer, so their screen is its
    // truth. A room has several writers, so everyone in it keeps checking.
    if (isInstructor && !subRoomGuid) return () => { cancelled = true; };
    const id = setInterval(pull, PULL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [live, sessionGuid, subRoomGuid, isInstructor, apply]);

  return { status, isLive: live };
}
